import { getTranslateByKey, renderErrorMessages } from "@/main/services/utils";
import React from "react";
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerProps,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseFormStateReturn
} from "react-hook-form";
import KogInputText from "./inputText";
import KogInputTextOutlined from "./inputTextOutlined";

type IControllerProps = Omit<ControllerProps, 'render' | 'name'>;
type IHTMLInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'number' | 'color' | 'size'>;
type IKogInputDimensionsMaskControlledProps<T extends FieldValues> = {
  id: string;
  name: FieldPath<T>;
  control: Control<T>;
  typeInput?: 'outlined' | 'filled';
  required?: boolean;
  errorMessageCustom?: string;
  controllerProps?: IControllerProps;
  label?: string;
  customInputProps?: IHTMLInputProps
  disabledMessage?: boolean;
  hideValue?: boolean;
  onChangeCustom?: (value: string) => void;
}
/**
 * Component to render a controlled input with dimensions mask.
 *
 * @param {string} id - Id of the input
 * @param {string} name - Name of the input
 * @param {Control<FieldValues>} control - Control of the form
 * @param {boolean} required - If true, the input will be required
 * @param {string} errorMessageCustom - Custom error message
 * @param {string} label - Label of the input
 * @param {IControllerProps} controllerProps - Props of the controller
 * @param {string} typeInput - Type of the input
 * @param {React.InputHTMLAttributes<HTMLInputElement>} customInputProps - Custom props of the input
 * @param {boolean} disabledMessage - If true, the error message will be disabled
 * @param {boolean} hideValue - If true, the value will be hidden
 * @param {Function} onChangeCustom - Function to be executed when the value changes
 * @example
 * const { control } = useForm(); //react-hoook-form
 *
 *  <KogInputDimensionsMaskControlled
 *    id='inputtest'
 *    name="inputtest"
 *    control={control}
 *  />
*/
const KogInputDimensionsMaskControlled = <T extends FieldValues>({
  id,
  name,
  control,
  required = false,
  errorMessageCustom,
  typeInput = 'filled',
  label = '',
  controllerProps = {},
  customInputProps = {},
  disabledMessage = false,
  onChangeCustom = () => { },
  hideValue = false,
}: IKogInputDimensionsMaskControlledProps<T>) => {
  function formatDimensions(value: number) {
    const parts = value.toString().match(/.{1,2}/g) || [];
    const formattedValue = parts.join('x');

    return formattedValue;
  }

  function validateDimensions(value: string) {
    const removingSpace = value.replace(/\s/g, '').toLowerCase();
    const splitDimensions = removingSpace.split('x');

    if (splitDimensions.length !== 3) {
      return getTranslateByKey('information.haveToFillHeightWidthDepth', true);
    }
    for (let i = 0; i < splitDimensions.length; i++) {
      const value = Number(splitDimensions[i]);
      if (isNaN(value) || !isFinite(value)) {
        return getTranslateByKey('information.haveToFillHeightWidthDepth', true);
      }
    }
    return true;
  }

  function maskDimensions(value: string) {
    const valueLoweCase = value.replace(/\s/g, '').toLowerCase();

    return valueLoweCase;
  }

  const renderInput = ({ field, fieldState: { error } }: {
    field: ControllerRenderProps<FieldValues, string>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<FieldValues>;
  }) => {
    /**
     * Notation:
     * If the value is a number, it's because a value that came from the database,
     */
    const validateValue = typeof field.value === "number" ? formatDimensions(field.value) : field.value;

    const onChangeInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const validateValue = maskDimensions(e.target.value);

      // Notation: This is to keep the cursor in the same position
      const hasLastIndexChar = e.target.selectionStart === e.target.value.length;
      const positionChanged = hasLastIndexChar ? validateValue.length : e.target.selectionStart;
      e.target.value = validateValue;
      e.target.setSelectionRange(positionChanged, positionChanged);

      field.onChange(validateValue);
      onChangeCustom(validateValue);
    }

    const baseInputProps: IHTMLInputProps = {
      ...field,
      ...customInputProps,
      id: id,
      required: required,
      value: hideValue ? '' : validateValue,
      onChange: onChangeInput,
    }

    switch (typeInput) {
      case 'outlined':
        return (
          <KogInputTextOutlined
            {...baseInputProps}
            error={!!error}
          />
        );
      case 'filled':
        return (
          <KogInputText
            {...baseInputProps}
            label={label}
            error={!!error}
            helperText={!disabledMessage && (errorMessageCustom || renderErrorMessages(label, error))}
          />
        );
      default:
        return (
          <input
            {...baseInputProps}
          />
        );
    }
  }
  const customControllerProps: IControllerProps = {
    ...controllerProps,
    ...({
      rules: {
        required,
        validate: validateDimensions
      }
    })
  };

  return (
    <Controller
      name={name}
      control={control as Control<FieldValues>}
      defaultValue={''}
      {...customControllerProps}
      render={renderInput}
    />
  );
};

export default KogInputDimensionsMaskControlled;
